/**
 * Get lib from https://github.com/jsillitoe/react-currency-input
 * adapted by Djeison Hart.
 */

import "./object-assign-polyfill";

import PropTypes from "prop-types";
import React, { Component } from "react";
import ReactDOM from "react-dom";
import mask from "./mask.js";

class InputCurrency extends Component {
	constructor(props) {
		super(props);
		this.prepareProps = this.prepareProps.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleFocus = this.handleFocus.bind(this);
		this.setSelectionRange = this.setSelectionRange.bind(this);
		this.state = this.prepareProps(this.props);

		this.inputSelectionStart = 1;
		this.inputSelectionEnd = 1;

		// Set input style
		this.style = ["form-control", "input-currency-component__input", props.className].join(" ");
	}

	/**
	 * Exposes the current masked value.
	 *
	 * @returns {String}
	 */
	getMaskedValue() {
		return this.state.maskedValue;
	}

	/**
	 * General function used to cleanup and define the final props used for rendering
	 * @returns {{ maskedValue: {String}, value: {Number}, customProps: {Object} }}
	 */
	prepareProps(props) {
		let customProps = { ...props }; // babeljs converts to Object.assign, then polyfills.
		delete customProps.onChange;
		delete customProps.onChangeEvent;
		delete customProps.value;
		delete customProps.decimalSeparator;
		delete customProps.thousandSeparator;
		delete customProps.precision;
		delete customProps.label;
		delete customProps.tooltip;
		delete customProps.tooltipDirection;
		delete customProps.className;
		delete customProps.allowNegative;
		delete customProps.allowEmpty;
		delete customProps.prefix;
		delete customProps.suffix;
		delete customProps.selectAllOnFocus;
		delete customProps.autoFocus;
		delete customProps.maxLength;
		delete customProps.maxValue;
		delete customProps.errorMessage;
		delete customProps.stepButtons;
		delete customProps.stepSteps;
		delete customProps.refInput;

		let initialValue = props.value;
		if (initialValue === null) {
			initialValue = props.allowEmpty ? null : "";
		} else {
			if (typeof initialValue == "string") {
				// Some people, when confronted with a problem, think "I know, I'll use regular expressions."
				// Now they have two problems.

				// Strip out thousand separators, prefix, and suffix, etc.
				if (props.thousandSeparator === ".") {
					// special handle the . thousand separator
					initialValue = initialValue.replace(/\./g, "");
				}

				if (props.decimalSeparator != ".") {
					// fix the decimal separator
					initialValue = initialValue.replace(new RegExp(props.decimalSeparator, "g"), ".");
				}

				//Strip out anything that is not a digit, -, or decimal separator
				initialValue = initialValue.replace(/[^0-9-.]/g, "");

				// now we can parse.
				initialValue = Number.parseFloat(initialValue);
			}
			initialValue = Number(initialValue).toLocaleString(undefined, {
				style: "decimal",
				minimumFractionDigits: props.precision,
				maximumFractionDigits: props.precision
			});
		}

		const { maskedValue, value } = mask(
			initialValue,
			props.precision,
			props.decimalSeparator,
			props.thousandSeparator,
			props.allowNegative,
			props.prefix,
			props.suffix
		);

		return { maskedValue, value, customProps };
	}

	/**
	 * Component lifecycle function.
	 * Invoked when a component is receiving new props. This method is not called for the initial render.
	 *
	 * @param nextProps
	 * @see https://facebook.github.io/react/docs/component-specs.html#updating-componentwillreceiveprops
	 */
	componentWillReceiveProps(nextProps) {
		this.setState(this.prepareProps(nextProps));
	}

	/**
	 * Component lifecycle function.
	 * @returns {XML}
	 * @see https://facebook.github.io/react/docs/react-component.html#componentdidmount
	 */
	componentDidMount() {
		let node = ReactDOM.findDOMNode(this.theInput);
		let selectionStart, selectionEnd;

		if (this.props.autoFocus) {
			this.theInput.focus();
			selectionEnd = this.state.maskedValue.length - this.props.suffix.length;
			selectionStart = selectionEnd;
		} else {
			selectionEnd = Math.min(
				node.selectionEnd,
				this.theInput.value.length - this.props.suffix.length
			);
			selectionStart = Math.min(node.selectionStart, selectionEnd);
		}

		this.setSelectionRange(node, selectionStart, selectionEnd);
	}

	/**
	 * Component lifecycle function
	 * @returns {XML}
	 * @see https://facebook.github.io/react/docs/react-component.html#componentwillupdate
	 */
	componentWillUpdate() {
		let node = ReactDOM.findDOMNode(this.theInput);
		this.inputSelectionStart = node.selectionStart;
		this.inputSelectionEnd = node.selectionEnd;
	}

	/**
	 * Component lifecycle function.
	 * @returns {XML}
	 * @see https://facebook.github.io/react/docs/react-component.html#componentdidupdate
	 */
	componentDidUpdate(prevProps, prevState) {
		const { decimalSeparator } = this.props;
		let node = ReactDOM.findDOMNode(this.theInput);
		let isNegative = (this.theInput.value.match(/-/g) || []).length % 2 === 1;
		let minPos = this.props.prefix.length + (isNegative ? 1 : 0);
		let selectionEnd = Math.max(minPos, this.theInput.value.length - this.props.suffix.length);
		let selectionStart = Math.max(minPos, selectionEnd);

		let regexEscapeRegex = /[-[\]{}()*+?.,\\^$|#\s]/g;
		let separatorsRegex = new RegExp(
			decimalSeparator.replace(regexEscapeRegex, "\\$&") +
				"|" +
				this.props.thousandSeparator.replace(regexEscapeRegex, "\\$&"),
			"g"
		);
		let currSeparatorCount = (this.state.maskedValue.match(separatorsRegex) || []).length;
		let prevSeparatorCount = (prevState.maskedValue.match(separatorsRegex) || []).length;
		let adjustment = Math.max(currSeparatorCount - prevSeparatorCount, 0);

		selectionEnd = selectionEnd + adjustment;
		selectionStart = selectionStart + adjustment;

		const precision = Number(this.props.precision);

		let baselength =
			this.props.suffix.length +
			this.props.prefix.length +
			(precision > 0 ? decimalSeparator.length : 0) + // if precision is 0 there will be no decimal part
			precision +
			1; // This is to account for the default '0' value that comes before the decimal separator

		if (this.state.maskedValue.length == baselength) {
			// if we are already at base length, position the cursor at the end.
			selectionEnd = this.theInput.value.length - this.props.suffix.length;
			selectionStart = selectionEnd;
		}

		this.setSelectionRange(node, selectionStart, selectionEnd);
		this.inputSelectionStart = selectionStart;
		this.inputSelectionEnd = selectionEnd;
	}

	/**
	 * Set selection range only if input is in focused state
	 * @param node DOMElement
	 * @param start number
	 * @param end number
	 */
	setSelectionRange(node, start, end) {
		if (document.activeElement === node) {
			node.setSelectionRange(start, end);
		}
	}

	/**
	 * onChange Event Handler
	 * @param event
	 */
	handleChange(event, stepValue = null) {
		const { maxLength, maxValue } = this.props,
			initValue = !!stepValue ? stepValue : event.target.value;

		!!event && event.preventDefault();

		let { maskedValue, value } = mask(
			initValue,
			this.props.precision,
			this.props.decimalSeparator,
			this.props.thousandSeparator,
			this.props.allowNegative,
			this.props.prefix,
			this.props.suffix
		);

		!!event && event.persist(); // fixes issue #23

		// Validate max length
		if (maxLength && maxLength > 0) {
			let digits;
			if(this.props["data-action"] === "simulator-miles-price"){
				digits = String(value.toFixed(2)).match(/\d/g) || ["0"];
			} else {
				digits = String(value).match(/\d/g) || ["0"];
			}
			if (digits.length > maxLength) {
				return false;
			}
		}

		// Validate max value
		if (maxValue && maxValue > 0) {
			if (value > maxValue) {
				value = maxValue;
				let m = mask(
					value.toFixed(this.props.precision),
					this.props.precision,
					this.props.decimalSeparator,
					this.props.thousandSeparator,
					this.props.allowNegative,
					this.props.prefix,
					this.props.suffix
				);
				value = m.value;
				maskedValue = m.maskedValue;
			}
		}

		let oldValue = this.state.value;

		if (!!stepValue) {
			this.setState({ maskedValue, value }, () => {
				if (oldValue !== value) {
					this.props.onChange(maskedValue, value, null);
					this.props.onChangeEvent(null, maskedValue, value);
				}
			});
		} else {
			this.setState({ maskedValue, value }, () => {
				if (oldValue !== value) {
					this.props.onChange(maskedValue, value, event);
					this.props.onChangeEvent(event, maskedValue, value);
				}
			});
		}

		window.setTimeout(() => {
			let inputLength = this.theInput.value.length;
			//Set cursor in the end of input
			this.theInput.setSelectionRange(inputLength + 2, inputLength + 2);
		}, 0);
	}

	/**
	 * onClick in step Handler
	 * @param step
	 */
	handleStep(step) {
		let stepValue = step === "plus" ? +this.props.stepSteps : +this.props.stepSteps / -1,
			fullValue = (this.state.value + stepValue).toFixed(this.props.precision);

		this.handleChange(null, fullValue);
	}

	/**
	 * onFocus Event Handler
	 * @param event
	 */
	handleFocus(event) {
		if (!this.theInput) return;

		//Whenever we receive focus check to see if the position is before the suffix, if not, move it.
		let selectionEnd = this.theInput.value.length - this.props.suffix.length;
		let isNegative = (this.theInput.value.match(/-/g) || []).length % 2 === 1;
		let selectionStart = this.props.prefix.length + (isNegative ? 1 : 0);
		this.props.selectAllOnFocus && event.target.setSelectionRange(selectionStart, selectionEnd);
		this.inputSelectionStart = selectionStart;
		this.inputSelectionEnd = selectionEnd;
	}

	handleBlur(event) {
		this.inputSelectionStart = 0;
		this.inputSelectionEnd = 0;
	}

	/**
	 * Component lifecycle function.
	 * @returns {XML}
	 * @see https://facebook.github.io/react/docs/component-specs.html#render
	 */
	render() {
		const {
			label,
			tooltip,
			tooltipDirection,
			tooltipColor,
			errorMessage,
			stepButtons
		} = this.props;
		const { maskedValue, error } = this.state;

		return (
			<div className="form-group input-currency-component">
				{!!label ? (
					<label className="input-currency-component__label">
						<span className="input-currency-component__label__span">{label}</span>
						{tooltip ? (
							<i
								className={`tooltip ${tooltipColor || "gray"}`}
								data-tooltip={tooltip}
								data-flow={tooltipDirection || "up"}
							>
								?
							</i>
						) : null}
					</label>
				) : (
					""
				)}

				<input
					ref={input => {
						this.theInput = input;
						if (this.props.refInput) {
							this.props.refInput(input);
						}
					}}
					type="text"
					pattern="\d*"
					inputMode="numeric"
					value={maskedValue}
					onChange={this.handleChange}
					onFocus={this.handleFocus}
					onMouseUp={this.handleFocus}
					className={`${this.style} ${
						errorMessage != undefined ? (errorMessage ? "input-invalid" : "input-valid") : ""
					}`}
					{...this.state.customProps}
				/>
				{stepButtons && (
					<div className="input-currency-component__stepButtons">
						<div
							className="input-currency-component__stepButtons--minus step-button"
							onClick={e => this.handleStep("minus")}
						>
							<i className="icon-reduction" />
						</div>
						<div
							className="input-currency-component__stepButtons--plus step-button"
							onClick={e => this.handleStep("plus")}
						>
							<i className="icon-addition" />
						</div>
					</div>
				)}
				{errorMessage != undefined &&
					errorMessage && <p className="text-right text-invalid">{errorMessage}</p>}
				{errorMessage != undefined && (
					<i
						className={`icon-${errorMessage ? "close" : "check"} ${
							stepButtons ? "validation-step" : ""
						} form-control-feedback`}
					/>
				)}
			</div>
		);
	}
}

/**
 * Prop validation.
 * @see https://facebook.github.io/react/docs/component-specs.html#proptypes
 */

InputCurrency.propTypes = {
	onChange: PropTypes.func,
	onValidate: PropTypes.func,
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	decimalSeparator: PropTypes.string,
	thousandSeparator: PropTypes.string,
	precision: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	allowNegative: PropTypes.bool,
	allowEmpty: PropTypes.bool,
	prefix: PropTypes.string,
	suffix: PropTypes.string,
	selectAllOnFocus: PropTypes.bool,
	maxLength: PropTypes.number,
	maxValue: PropTypes.number,
	errorMessage: PropTypes.string,
	stepButtons: PropTypes.bool,
	stepSteps: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

InputCurrency.defaultProps = {
	onChange: function(maskValue, value, event) {
		/*no-op*/
	},
	onChangeEvent: function(event, maskValue, value) {
		/*no-op*/
	},
	autoFocus: false,
	allowEmpty: true,
	value: "",
	decimalSeparator: ".",
	thousandSeparator: ",",
	precision: "2",
	allowNegative: false,
	prefix: "",
	suffix: "",
	selectAllOnFocus: false,
	stepButtons: false,
	stepSteps: 1
};

export default InputCurrency;
